/**
 * Wishlist Drawer — Dynamically renders wishlist items from localStorage
 * into the wishlist-drawer <theme-drawer> panel with instant display and zero latency.
 */
class WishlistDrawer {
  constructor() {
    this.wishlistKey = 'taiba_luxury_wishlist';
    this.drawer = document.getElementById('wishlist-drawer');
    this.grid = document.getElementById('wishlist-drawer-grid');
    this.loading = document.getElementById('wishlist-drawer-loading');
    this.empty = document.getElementById('wishlist-drawer-empty');

    this.init();
  }

  init() {
    this.listenForOpen();
    this.listenForUpdates();
  }

  listenForOpen() {
    if (this.drawer) {
      this.drawer.addEventListener('theme-drawer:open', () => {
        this.render();
      });
    }

    // Also trigger render if user clicks any wishlist drawer trigger
    document.querySelectorAll('[on\\:click*="#wishlist-drawer"], [aria-controls="wishlist-drawer"]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        setTimeout(() => this.render(), 50);
      });
    });
  }

  listenForUpdates() {
    window.addEventListener('wishlist:updated', () => {
      // Re-render if drawer is currently open
      if (this.drawer && this.drawer.hasAttribute('open')) {
        this.render();
      }
    });
  }

  /**
   * @returns {Array<Object>}
   */
  getWishlist() {
    if (window.wishlistManager && Array.isArray(window.wishlistManager.wishlist)) {
      return window.wishlistManager.wishlist;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(this.wishlistKey) || '[]');
      return stored.map((item) => {
        if (typeof item === 'string') {
          return {
            handle: item,
            title: item.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            url: `/products/${item}`,
            image: '',
            formatted_price: ''
          };
        }
        return item;
      });
    } catch (e) {
      return [];
    }
  }

  /**
   * Fetches product data in background if an item only has a handle without metadata.
   * @param {string} handle
   */
  async fetchProductFallback(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  /**
   * Encodes HTML special characters to prevent DOM-based XSS.
   * @param {string} str
   * @returns {string}
   */
  escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Validates and sanitizes URLs to ensure only safe relative paths or http/https links are rendered.
   * @param {string} url
   * @returns {string}
   */
  sanitizeURL(url) {
    if (!url) return '#';
    const clean = String(url).trim();
    if (clean.startsWith('/') || clean.startsWith('http://') || clean.startsWith('https://')) {
      return clean.replace(/"/g, '%22').replace(/'/g, '%27');
    }
    return '#';
  }

  async render() {
    if (!this.grid || !this.loading || !this.empty) return;

    const wishlist = this.getWishlist();

    if (wishlist.length === 0) {
      this.loading.style.display = 'none';
      this.grid.style.display = 'none';
      this.empty.style.display = 'block';
      return;
    }

    this.loading.style.display = 'none';
    this.empty.style.display = 'none';
    this.grid.style.display = 'flex';
    this.grid.innerHTML = '';

    // Render all items immediately using stored metadata
    wishlist.forEach((item) => {
      const handle = typeof item === 'string' ? item : item.handle;
      if (!handle) return;

      const title = item.title || handle.replace(/-/g, ' ');
      const price = item.formatted_price || (item.price ? (item.price / 100).toFixed(2) + ' AED' : '');
      const imgUrl = item.image || '';
      const url = item.url || `/products/${handle}`;

      const safeHandle = this.escapeHTML(handle);
      const safeTitle = this.escapeHTML(title);
      const safePrice = this.escapeHTML(price);
      const safeImgUrl = this.sanitizeURL(imgUrl);
      const safeUrl = this.sanitizeURL(url);

      const itemHTML = `
        <div class="wishlist-drawer__item" data-handle="${safeHandle}">
          <a href="${safeUrl}" class="wishlist-drawer__item-image">
            ${safeImgUrl ? `<img src="${safeImgUrl}" alt="${safeTitle}" loading="lazy" width="72" height="72">` : `<div style="width:72px;height:72px;background:#f5f5f5;display:flex;align-items:center;justify-content:center;color:#ccc;"><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg></div>`}
          </a>
          <div class="wishlist-drawer__item-info">
            <a href="${safeUrl}" style="text-decoration: none; color: inherit;">
              <p class="wishlist-drawer__item-title">${safeTitle}</p>
            </a>
            ${safePrice ? `<p class="wishlist-drawer__item-price">${safePrice}</p>` : ''}
            <a href="${safeUrl}" class="wishlist-drawer__item-link" style="display: inline-block; margin-top: 6px; font-size: 0.75rem; color: #b28b5d; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; text-decoration: none;">View Piece &rarr;</a>
          </div>
          <button
            type="button"
            class="wishlist-drawer__item-remove"
            data-handle="${safeHandle}"
            aria-label="Remove ${safeTitle} from wishlist"
          >
            <svg viewBox="0 0 14 14" width="10" height="10">
              <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      `;
      this.grid.insertAdjacentHTML('beforeend', itemHTML);
    });

    // Attach remove handlers
    this.grid.querySelectorAll('.wishlist-drawer__item-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = btn.dataset.handle;
        if (!handle) return;

        // Smooth fade out removal animation
        const itemEl = btn.closest('.wishlist-drawer__item');
        if (itemEl) {
          itemEl.style.transition = 'opacity 0.25s ease, transform 0.25s ease, max-height 0.25s ease';
          itemEl.style.opacity = '0';
          itemEl.style.transform = 'translateX(20px)';
          setTimeout(() => {
            this.removeItem(handle);
          }, 250);
        } else {
          this.removeItem(handle);
        }
      });
    });
  }

  /**
   * @param {string} handle
   */
  removeItem(handle) {
    if (window.wishlistManager) {
      window.wishlistManager.removeFromWishlist(handle);
    } else {
      let wishlist = this.getWishlist();
      wishlist = wishlist.filter((item) => {
        const h = typeof item === 'string' ? item : item.handle;
        return h !== handle;
      });
      localStorage.setItem(this.wishlistKey, JSON.stringify(wishlist));
    }
    this.render();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistDrawer = new WishlistDrawer();
  });
} else {
  window.wishlistDrawer = new WishlistDrawer();
}
